import { createI18n } from 'vue-i18n'

import { enEnhancements, zhEnhancements } from './enhancements'
import { enNodeDisplay, zhNodeDisplay } from './node-display'

const en = {
  ...enEnhancements,
  ...enNodeDisplay,
  brand: 'Pulsewatch',
  common: {
    apply: 'Apply',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    save: 'Save',
    saving: 'Saving...',
    deleting: 'Deleting...',
    loading: 'Loading...',
    enabled: 'Enabled',
    disabled: 'Disabled',
    back: 'Back',
    tryAgain: 'Try again',
    perSecond: '/s',
    notAvailable: '—',
  },
  nav: {
    dashboard: 'Dashboard',
    uptime: 'Uptime Leaderboard',
    appearance: 'Appearance',
    alerts: 'Alert rules',
    admin: 'Admin settings',
    signOut: 'Sign out',
    monitorOnly: 'Monitor only · no command channel',
    dashboardAria: 'Pulsewatch dashboard',
    settings: 'Settings',
    lightMode: 'Use light mode',
    darkMode: 'Use dark mode',
    openSettings: 'Open settings',
  },
  status: { online: 'Online', offline: 'Offline' },
  time: {
    never: 'Never seen',
    unknown: 'Unknown',
    day: '{count}d',
    hour: '{count}h',
    minute: '{count}m',
    second: '{count}s',
  },
  errors: {
    requestFailed: 'Request failed ({status})',
    apiUnavailable: 'The monitoring API is unavailable',
    loadNodes: 'Unable to load monitored nodes',
    loadNode: 'Unable to load node history',
    openNode: 'Unable to open this node',
    adminTokenRequired: 'Enter an administrator token',
    invalidRange: 'Choose a valid custom range with the start before the end.',
    saveAppearance: 'Unable to save appearance settings.',
    loadSettings: 'Unable to load settings.',
    saveRules: 'Unable to save alert rules.',
    invalidCooldown: 'Enter a whole-number cooldown from 1 to 10080 minutes.',
    enroll: 'Unable to enroll this node',
    updateNode: 'Unable to update this node.',
    deleteNode: 'Unable to delete this node.',
    loadAdmin: 'Unable to load administrator settings.',
    saveEmailSystem: 'Unable to save email system settings.',
    testEmail: 'Unable to send the test email.',
    saveBackupSettings: 'Unable to save backup settings.',
    createBackup: 'Unable to start a backup.',
    downloadBackup: 'Unable to download this backup.',
    chartExport: 'Unable to export this chart.',
  },
  dashboard: {
    eyebrow: 'Fleet overview',
    title: 'Infrastructure at a glance',
    subtitle: "Status is derived from server receipt time, never from a node's clock.",
    live: 'Live stream',
    polling: 'Polling',
    updated: 'updated {time}',
    enroll: 'Enroll node',
    monitored: 'Monitored nodes',
    onlineNow: 'Online now',
    of: 'of {count}',
    averageCpu: 'Fleet average CPU',
    onlineLabel: 'online',
    nodes: 'Nodes',
    filterAria: 'Filter by status',
    all: 'All statuses',
    online: 'Online',
    offline: 'Offline',
    searchAria: 'Search nodes',
    searchPlaceholder: 'Search name or ID',
    unavailable: 'The monitoring API is unavailable',
    noMatch: 'No matching nodes',
    noNodes: 'No nodes enrolled yet',
    noMatchMessage: 'Try a different name or status filter.',
    noNodesMessage: 'Enroll a node, then start its read-only agent.',
    enrollFirst: 'Enroll first node',
    suggestedName: 'node-{count}',
  },
  card: {
    node: 'Node',
    edit: 'Edit node',
    delete: 'Delete node',
    cpu: 'CPU',
    memory: 'Memory',
    storage: 'Storage',
    network: 'Network',
    uptime: 'Uptime',
    latency: 'Latency',
    waiting: 'Waiting for the first sample',
    seen: 'Seen {time}',
    inspect: 'Inspect',
  },
  nodeEdit: {
    eyebrow: 'Node settings',
    title: 'Edit node',
    name: 'Node name',
    samplingRate: 'Sampling rate',
    samplingRateHint: 'Expected interval between agent metric pushes.',
    seconds: 'seconds',
    remarks: 'Remarks',
    labels: 'Labels',
    labelHint: 'One key=value label per line',
    labelPlaceholder: 'environment=production\nregion=us-east',
    save: 'Save changes',
    enterName: 'Enter a node name.',
    invalidSamplingRate: 'Sampling rate must be between 1 and 3,600 seconds.',
    invalidLabel: 'Invalid label "{line}". Use key=value.',
    invalidKey: 'Invalid label key "{key}".',
    reserved: 'Use the Remarks field instead of a remarks label.',
    longValue: 'Label "{key}" exceeds 256 characters.',
    duplicate: 'Duplicate label key "{key}".',
    longRemarks: 'Remarks cannot exceed 256 characters.',
    tooMany: 'A node can have at most 32 labels, including remarks.',
    closeAria: 'Close edit dialog',
  },
  nodeDelete: {
    title: 'Delete node',
    description: 'Delete {name} and all of its stored metrics? This action cannot be undone.',
    confirm: 'Delete node',
    closeAria: 'Close delete dialog',
  },
  enrollment: {
    newNode: 'New monitored node',
    credentials: 'Credentials generated',
    title: 'Enroll a node',
    install: 'Install {name}',
    closeAria: 'Close enrollment',
    name: 'Node name',
    namePlaceholder: 'edge-02',
    interval: 'Collection interval',
    seconds: '{count} seconds',
    generate: 'Generate credentials',
    generating: 'Generating...',
    enterName: 'Enter a node name',
    tokenWarning:
      'Store this token now; it cannot be retrieved later. Closing this window permanently clears it from the UI.',
    agentId: 'Agent ID',
    token: 'Token',
    server: 'Server',
    copyAgent: 'Copy agent ID',
    copyToken: 'Copy token',
    quickInstall: 'Quick install',
    copyCommand: 'Copy install command',
    installDetail:
      'Run once in a private root shell. The installer verifies the binary checksum, writes the credential file with mode 0600, and enables the hardened systemd service.',
    identity: 'Identity',
    privileges: 'Privileges',
    memory: 'Memory',
    done: 'Done',
    closeWarning: 'This token cannot be shown again after closing. Continue?',
  },
  login: {
    eyebrow: 'Administrator access',
    title: 'Pulsewatch',
    subtitle: 'Sign in to inspect monitored infrastructure.',
    token: 'Administrator token',
    placeholder: 'Paste administrator token',
    submit: 'Sign in',
    signingIn: 'Signing in...',
    security: 'The token stays in this browser tab and is never placed in a URL.',
    pure: 'Pure monitoring',
    headline: 'Clear signals. Zero privileges.',
    description:
      'A read-only view across your infrastructure. Agents can report health, but the server has no path to execute commands on a node.',
    attributes: 'SERVER-TIMED · SECURE ACCESS · READ-ONLY',
    open: 'Open your control room',
    instruction: 'Sign in to view your nodes and manage monitoring.',
    adminToken: 'Admin token',
    paste: 'Paste token',
    hide: 'Hide',
    show: 'Show',
    verifying: 'Verifying...',
    continue: 'Continue securely',
    storage:
      'Pulsewatch never stores this credential in persistent browser storage. Close the tab to end the session.',
  },
  notFound: {
    eyebrow: '404',
    title: 'Signal not found',
    message: 'The page you requested does not exist.',
    back: 'Return to dashboard',
  },
  chart: {
    aria: 'Time series chart',
    noData: 'No samples in this range',
    display: 'Line display',
    smooth: 'Smooth',
    raw: 'Raw',
    smoothHint: 'Add a moving-average trend while retaining the original samples',
    rawHint: 'Show the original collected samples',
    showPercentiles: 'Show 95p / 99p',
    hidePercentiles: 'Hide 95p / 99p',
    p95: '95p total',
    p99: '99p total',
    milliseconds: '{value} ms',
    downloadHighRes: 'Download high-res chart',
    exportSelectRange: 'Select export time range',
    exportHint: 'Raw, high-sampling-rate data will be used for the selected period.',
    exportDownload: 'Download chart',
  },
  uptime: {
    eyebrow: 'Fleet health',
    title: 'Uptime Leaderboard',
    subtitle: 'Server-observed heartbeat availability for the selected period.',
    node: 'Node',
    uptimePercent: 'Uptime',
    onlineTime: 'Online',
    offlineTime: 'Offline',
    monitoredSince: 'Monitored since',
    notMonitored: 'Not yet monitored in this period',
    noNodes: 'No nodes to display',
    sortByUptime: 'Sort by uptime',
    sortByName: 'Sort by name',
    timeline: 'Daily availability',
    excellent: 'Excellent',
    degraded: 'Degraded',
    poor: 'Poor',
    average: 'Fleet average uptime',
    sort: 'Sort',
  },
  availability: {
    title: 'Availability',
    detail: 'Server-observed heartbeat coverage for the selected range',
    uptime: 'Uptime',
    online: 'Online {duration}',
    offline: 'Offline {duration}',
    timelineLabel: 'Node availability timeline',
    segmentLabel: '{start} to {end}: {status}, {uptime} uptime',
    status: {
      online: 'Online',
      offline: 'Offline',
      degraded: 'Partially online',
      unknown: 'Not monitored',
    },
  },
  node: {
    allNodes: 'All nodes',
    detail: 'Node detail',
    serverUptime: 'Server uptime',
    lastSample: 'Last sample',
    controllerLatency: 'Controller latency',
    latencyDetail: 'Authenticated HTTP round-trip time to this Pulsewatch controller',
    cpuUtilization: 'CPU utilization',
    user: 'User',
    memory: 'Memory',
    storage: 'Storage',
    used: 'Used',
    totalNetworkRate: 'Total network rate',
    interfaces: '{count} interface | {count} interfaces',
    history: 'History',
    signals: 'Resource signals',
    timeRange: 'Time range',
    lastHour: 'Last hour',
    last6Hours: 'Last 6 hours',
    last24Hours: 'Last 24 hours',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    customRange: 'Custom range',
    from: 'From',
    to: 'To',
    refreshing: 'Refreshing...',
    cpu: 'CPU',
    cpuDetail: 'User, system, and total utilization',
    total: 'Total',
    system: 'System',
    memoryDetail: 'Physical RAM and swap utilization',
    ram: 'RAM',
    swap: 'Swap',
    network: 'Network throughput',
    networkDetail: 'Aggregate receive and transmit rates',
    receive: 'Receive',
    transmit: 'Transmit',
    totalTraffic: 'Total traffic',
    disk: 'Disk throughput',
    diskDetail: 'Aggregate physical-drive read and write rates',
    read: 'Read',
    write: 'Write',
    load: 'System load',
    loadDetail: 'One, five, and fifteen-minute load averages',
    oneMin: '1 min',
    fiveMin: '5 min',
    fifteenMin: '15 min',
    analytics: 'Analytics mode',
    intelligence: 'Capacity intelligence',
    analyticsDisabled: 'Advanced analytics are disabled',
    analyticsDisabledMessage:
      'Switch the server to analytics mode on 4-core, 8 GB or larger hardware to enable percentiles, forecasts, and anomaly profiling.',
    p95Bandwidth: '95th percentile bandwidth',
    p95Detail: 'Billing-oriented peak, top 5% removed',
    p99Bandwidth: '99th percentile bandwidth',
    p99Detail: 'Near-maximum sustained throughput',
    anomalies: 'Anomalies detected',
    anomaliesDetail: 'Statistical outliers in this range',
    forecasts: 'Resource forecasts',
    perDay: '{value} / day',
    insufficient: 'Insufficient trend',
    pointsDay: '{value} points/day',
    forecastEmpty: 'More history is needed for a reliable forecast.',
    peaks: 'Peak usage windows',
    around: 'Around {time}',
    peakEmpty: 'No recurring peak window is available yet.',
    outliers: 'Recent outliers',
    time: 'Time',
    metric: 'Metric',
    value: 'Value',
    deviation: 'Deviation',
    resourceTarget: '{resource} · {target}',
    analyticsLabels: {
      memory: 'Memory',
      disk: 'Disk',
      cpuPercent: 'CPU utilization',
      memoryPercent: 'Memory usage',
      diskMaxPercent: 'Disk usage',
      networkBytesPerSecond: 'Network throughput',
      utilization: 'Utilization',
      insufficientData: 'Insufficient data',
      notExhausting: 'Not approaching capacity',
      alreadyAtOrAboveCapacity: 'At or above capacity',
      forecast: 'Forecast available',
      stable: 'Stable',
      unknown: 'Other metric',
    },
  },
  appearance: {
    eyebrow: 'Settings',
    title: 'Appearance',
    subtitle: 'Customize the dashboard for every administrator session.',
    theme: 'Color mode',
    system: 'System',
    light: 'Light',
    dark: 'Dark',
    language: 'Language',
    english: 'English',
    chinese: '简体中文',
    assets: 'Brand assets',
    favicon: 'Favicon URL',
    faviconPlaceholder: 'https://assets.example.com/favicon.ico',
    background: 'Dashboard background URL',
    backgroundPlaceholder: 'https://assets.example.com/background.webp',
    urlHint: 'Use an HTTPS URL or a same-origin path. Leave blank to restore the default.',
    save: 'Save appearance',
    saved: 'Appearance saved.',
  },
  alerts: {
    eyebrow: 'Settings',
    title: 'Alert rules',
    subtitle: 'Configure global trigger conditions and email delivery.',
    rule: 'Rule',
    condition: 'Condition',
    hold: 'For',
    strategy: 'Notification strategy',
    cpu: 'CPU utilization',
    memory: 'Memory usage',
    disk: 'Storage usage',
    network: 'Network throughput',
    offline: 'Node offline',
    greaterThan: 'greater than',
    duration: '{count} min',
    immediate: 'Immediately',
    bytesPerSecond: 'bytes/s',
    megabytesPerSecond: 'MB/s',
    percentUnit: '%',
    minuteUnit: 'min',
    recipients: 'Email recipients',
    recipientsPlaceholder: "ops{'@'}example.com, oncall{'@'}example.com",
    recipientsHint:
      'Comma-separated addresses. SMTP delivery must also be configured on the server.',
    smtpReady: 'SMTP delivery is configured.',
    smtpMissing: 'SMTP delivery is not configured; events will still be recorded.',
    cooldown: 'Repeat notification cooldown',
    cooldownHint: 'Applies only to rules using continuous repeat.',
    stateChange: 'State change only',
    continuous: 'Continuous repeat',
    minutes: 'minutes',
    save: 'Save alert rules',
    saved: 'Alert rules saved.',
    toggleRule: 'Enable or disable {rule}',
    thresholdFor: 'Threshold for {rule}',
    durationFor: 'Hold duration for {rule}',
    strategyFor: 'Notification strategy for {rule}',
  },
  admin: {
    eyebrow: 'System administration',
    title: 'Admin settings',
    subtitle: 'Manage outbound email and protected system backups.',
    emailTab: 'Email system',
    backupTab: 'Backups',
    smtpTitle: 'SMTP configuration',
    smtpSubtitle: 'Credentials are write-only and are never returned by the API.',
    smtpHost: 'SMTP host',
    smtpPort: 'Port',
    senderAddress: 'Sender address',
    smtpUsername: 'Username',
    smtpPassword: 'Password',
    secretStored: 'Stored secret (leave blank to retain)',
    connectionSecurity: 'Connection security',
    implicitTls: 'Implicit TLS (usually port 465)',
    starttls: 'STARTTLS (usually port 587)',
    noEncryption: 'None (not recommended)',
    clearStoredPassword: 'Clear stored password',
    testRecipient: 'Test recipient',
    testRecipientPlaceholder: "administrator{'@'}example.com",
    testRecipientHint: 'The test uses the current form values without saving them.',
    templateTitle: 'Email templates',
    templateSubtitle: 'Failures and recoveries use independent HTML templates.',
    alertTemplate: 'Alert template',
    recoveryTemplate: 'Recovery template',
    placeholders: 'Available placeholders',
    insertPlaceholder: 'Insert placeholder at cursor',
    templateEditor: 'HTML email template editor',
    alertTemplateEditor: 'Alert HTML template editor',
    recoveryTemplateEditor: 'Recovery HTML template editor',
    livePreview: 'Live preview',
    previewTitle: 'Rendered alert email preview',
    alertPreviewTitle: 'Rendered alert email preview',
    recoveryPreviewTitle: 'Rendered recovery email preview',
    previewNode: 'Edge node 01',
    previewReason: 'CPU utilization above threshold',
    previewAlertReason: 'CPU utilization breached: 94.2% >= effective threshold 90.0%.',
    previewRecoveryReason: 'CPU utilization recovered: 42.0% < effective threshold 90.0%.',
    emailSaved: 'Email system settings saved.',
    saveEmail: 'Save email system',
    sendTestEmail: 'Send test email',
    testingEmail: 'Sending test...',
    testEmailSent: 'Test email sent to {recipient}.',
    manualBackup: 'Immediate backup',
    manualBackupDetail: 'Create a consistent database and retained-history bundle now.',
    startingBackup: 'Starting...',
    backupNow: 'Back up now',
    backupConfiguration: 'Backup configuration',
    scheduledBackups: 'Enable scheduled backups',
    everyDays: 'Run every X days',
    storageTarget: 'Storage target',
    localBackup: 'Local backup',
    remoteBackup: 'Remote backup',
    endpointUrl: 'Endpoint URL',
    region: 'Region',
    bucketName: 'Bucket name',
    accessKey: 'Access key',
    secretKey: 'Secret key',
    clearStoredSecret: 'Clear stored secret key',
    saveBackup: 'Save backup settings',
    backupSettingsSaved: 'Backup settings saved.',
    backupQueued: 'Backup started. History will update automatically.',
    backupHistory: 'Backup history',
    file: 'File',
    created: 'Created',
    target: 'Target',
    size: 'Size',
    statusLabel: 'Status',
    actions: 'Actions',
    download: 'Download',
    noBackups: 'No backups have been created yet.',
    status: {
      pending: 'Pending',
      running: 'Running',
      completed: 'Completed',
      failed: 'Failed',
    },
  },
}

const zhCN = {
  ...zhEnhancements,
  ...zhNodeDisplay,
  brand: 'Pulsewatch',
  common: {
    apply: '应用',
    cancel: '取消',
    close: '关闭',
    delete: '删除',
    edit: '编辑',
    save: '保存',
    saving: '保存中...',
    deleting: '删除中...',
    loading: '加载中...',
    enabled: '启用',
    disabled: '停用',
    back: '返回',
    tryAgain: '重试',
    perSecond: '/秒',
    notAvailable: '—',
  },
  nav: {
    dashboard: '仪表盘',
    uptime: '可用率排行榜',
    appearance: '外观',
    alerts: '告警规则',
    admin: '管理设置',
    signOut: '退出',
    monitorOnly: '仅监控 · 无命令通道',
    dashboardAria: 'Pulsewatch 仪表盘',
    settings: '设置',
    lightMode: '使用浅色模式',
    darkMode: '使用深色模式',
    openSettings: '打开设置',
  },
  status: { online: '在线', offline: '离线' },
  time: {
    never: '从未上报',
    unknown: '未知',
    day: '{count}天',
    hour: '{count}小时',
    minute: '{count}分钟',
    second: '{count}秒',
  },
  errors: {
    requestFailed: '请求失败（{status}）',
    apiUnavailable: '监控 API 当前不可用',
    loadNodes: '无法加载监控节点',
    loadNode: '无法加载节点历史数据',
    openNode: '无法打开此节点',
    adminTokenRequired: '请输入管理员令牌',
    invalidRange: '请选择有效的时间范围，开始时间必须早于结束时间。',
    saveAppearance: '无法保存外观设置。',
    loadSettings: '无法加载设置。',
    saveRules: '无法保存告警规则。',
    invalidCooldown: '请输入 1 到 10080 之间的整数分钟。',
    enroll: '无法添加此节点',
    updateNode: '无法更新此节点。',
    deleteNode: '无法删除此节点。',
    loadAdmin: '无法加载管理员设置。',
    saveEmailSystem: '无法保存邮件系统设置。',
    testEmail: '无法发送测试邮件。',
    saveBackupSettings: '无法保存备份设置。',
    createBackup: '无法启动备份。',
    downloadBackup: '无法下载此备份。',
    chartExport: '无法导出此图表。',
  },
  dashboard: {
    eyebrow: '节点总览',
    title: '基础设施概览',
    subtitle: '状态以服务器接收时间为准，不依赖节点时钟。',
    live: '实时连接',
    polling: '轮询',
    updated: '更新于 {time}',
    enroll: '添加节点',
    monitored: '监控节点',
    onlineNow: '当前在线',
    of: '共 {count} 个',
    averageCpu: '平均 CPU',
    onlineLabel: '在线',
    nodes: '节点',
    filterAria: '按状态筛选',
    all: '全部状态',
    online: '在线',
    offline: '离线',
    searchPlaceholder: '搜索名称或 ID',
    searchAria: '搜索节点',
    unavailable: '监控 API 当前不可用',
    noMatch: '没有匹配的节点',
    noNodes: '尚未添加节点',
    noMatchMessage: '请尝试其他名称或状态筛选条件。',
    noNodesMessage: '添加节点后，启动其只读代理程序。',
    enrollFirst: '添加第一个节点',
    suggestedName: '节点-{count}',
  },
  card: {
    node: '节点',
    edit: '编辑节点',
    delete: '删除节点',
    cpu: 'CPU',
    memory: '内存',
    storage: '存储',
    network: '网络',
    uptime: '运行时间',
    latency: '延迟',
    waiting: '等待首个数据样本',
    seen: '{time}上报',
    inspect: '查看',
  },
  nodeEdit: {
    eyebrow: '节点设置',
    title: '编辑节点',
    name: '节点名称',
    samplingRate: '采样频率',
    samplingRateHint: '代理上报指标的预期间隔。',
    seconds: '秒',
    remarks: '备注',
    labels: '标签',
    labelHint: '每行填写一个 key=value 标签',
    labelPlaceholder: 'environment=production\nregion=cn-east',
    save: '保存更改',
    enterName: '请输入节点名称。',
    invalidSamplingRate: '采样频率必须在 1 到 3,600 秒之间。',
    invalidLabel: '标签“{line}”无效，请使用 key=value 格式。',
    invalidKey: '标签键“{key}”无效。',
    reserved: '请使用备注字段，不要添加 remarks 标签。',
    longValue: '标签“{key}”超过 256 个字符。',
    duplicate: '标签键“{key}”重复。',
    longRemarks: '备注不能超过 256 个字符。',
    tooMany: '每个节点最多可有 32 个标签，包括备注。',
    closeAria: '关闭编辑节点对话框',
  },
  nodeDelete: {
    title: '删除节点',
    description: '确定删除 {name} 及其全部历史指标吗？此操作无法撤销。',
    confirm: '删除节点',
    closeAria: '关闭删除节点对话框',
  },
  enrollment: {
    newNode: '新增监控节点',
    credentials: '凭据已生成',
    title: '添加节点',
    install: '安装 {name}',
    closeAria: '关闭节点添加对话框',
    name: '节点名称',
    namePlaceholder: '边缘节点-02',
    interval: '采集间隔',
    seconds: '{count} 秒',
    generate: '生成凭据',
    generating: '正在生成...',
    enterName: '请输入节点名称',
    tokenWarning: '请立即妥善保存此令牌，之后无法再次获取。关闭窗口后，令牌将从界面中永久清除。',
    agentId: '代理 ID',
    token: '令牌',
    server: '服务器',
    copyAgent: '复制代理 ID',
    copyToken: '复制令牌',
    quickInstall: '快速安装',
    copyCommand: '复制安装命令',
    installDetail:
      '请在目标节点的私有 root shell 中运行一次。安装程序会校验二进制文件、以 0600 权限写入凭据，并启用加固后的 systemd 服务。',
    identity: '运行身份',
    privileges: '权限限制',
    memory: '内存限制',
    done: '完成',
    closeWarning: '关闭后将无法再次查看此令牌。确定继续吗？',
  },
  login: {
    eyebrow: '管理员访问',
    title: 'Pulsewatch',
    subtitle: '登录以查看受监控的基础设施。',
    token: '管理员令牌',
    placeholder: '粘贴管理员令牌',
    submit: '登录',
    signingIn: '正在登录...',
    security: '令牌仅保留在当前浏览器标签页中，不会写入 URL。',
    pure: '纯监控',
    headline: '信号清晰，零节点权限。',
    description: '以只读方式查看基础设施。代理只能上报状态，服务器无法在节点上执行命令。',
    attributes: '服务器计时 · 安全登录 · 只读',
    open: '打开监控台',
    instruction: '登录后查看节点状态并管理监控设置。',
    adminToken: '管理员令牌',
    paste: '粘贴令牌',
    hide: '隐藏',
    show: '显示',
    verifying: '正在验证...',
    continue: '安全登录',
    storage: 'Pulsewatch 不会将此凭据写入浏览器持久存储。关闭标签页即可结束会话。',
  },
  notFound: {
    eyebrow: '404',
    title: '未找到页面',
    message: '您请求的页面不存在。',
    back: '返回仪表盘',
  },
  chart: {
    aria: '时间序列图',
    noData: '此时间范围内没有样本',
    display: '曲线显示',
    smooth: '平滑',
    raw: '原始',
    smoothHint: '保留原始样本并叠加移动平均趋势线',
    rawHint: '显示原始采集样本',
    showPercentiles: '显示 95p / 99p',
    hidePercentiles: '隐藏 95p / 99p',
    p95: '总吞吐量 95p',
    p99: '总吞吐量 99p',
    milliseconds: '{value} 毫秒',
    downloadHighRes: '下载高清图表',
    exportSelectRange: '选择导出时间范围',
    exportHint: '所选时间段将使用原始高采样率数据。',
    exportDownload: '下载图表',
  },
  uptime: {
    eyebrow: '节点健康',
    title: '可用率排行榜',
    subtitle: '所选时间范围内服务器观测到的心跳覆盖情况。',
    node: '节点',
    uptimePercent: '在线率',
    onlineTime: '在线时间',
    offlineTime: '离线时间',
    monitoredSince: '监控开始时间',
    notMonitored: '此时间段内尚未监控',
    noNodes: '没有可显示的节点',
    sortByUptime: '按在线率排序',
    sortByName: '按名称排序',
    timeline: '每日可用性',
    excellent: '优秀',
    degraded: '较差',
    poor: '很差',
    average: '节点平均在线率',
    sort: '排序',
  },
  availability: {
    title: '可用性',
    detail: '所选时间范围内由服务器观测的心跳覆盖情况',
    uptime: '在线率',
    online: '在线 {duration}',
    offline: '离线 {duration}',
    timelineLabel: '节点可用性时间线',
    segmentLabel: '{start} 至 {end}：{status}，在线率 {uptime}',
    status: {
      online: '在线',
      offline: '离线',
      degraded: '部分在线',
      unknown: '尚未监控',
    },
  },
  appearance: {
    eyebrow: '设置',
    title: '外观',
    subtitle: '为所有管理员会话自定义仪表盘。',
    theme: '颜色模式',
    system: '跟随系统',
    light: '浅色',
    dark: '深色',
    language: '语言',
    english: 'English',
    chinese: '简体中文',
    assets: '品牌资源',
    favicon: '网站图标 URL',
    faviconPlaceholder: 'https://assets.example.com/favicon.ico',
    background: '仪表盘背景 URL',
    backgroundPlaceholder: 'https://assets.example.com/background.webp',
    urlHint: '使用 HTTPS URL 或同源绝对路径。留空可恢复默认。',
    save: '保存外观',
    saved: '外观已保存。',
  },
  alerts: {
    eyebrow: '设置',
    title: '告警规则',
    subtitle: '配置全局触发条件和邮件通知。',
    rule: '规则',
    condition: '条件',
    hold: '持续时间',
    strategy: '通知策略',
    cpu: 'CPU 使用率',
    memory: '内存使用率',
    disk: '存储使用率',
    network: '网络吞吐量',
    offline: '节点离线',
    greaterThan: '大于',
    duration: '{count} 分钟',
    immediate: '立即',
    bytesPerSecond: '字节/秒',
    megabytesPerSecond: 'MB/秒',
    percentUnit: '%',
    minuteUnit: '分钟',
    recipients: '邮件收件人',
    recipientsPlaceholder: "ops{'@'}example.com, oncall{'@'}example.com",
    recipientsHint: '使用逗号分隔。服务器还必须配置 SMTP。',
    smtpReady: 'SMTP 邮件通知已配置。',
    smtpMissing: 'SMTP 未配置；告警事件仍会记录。',
    cooldown: '重复通知冷却时间',
    cooldownHint: '仅适用于使用连续重复策略的规则。',
    stateChange: '仅状态变化',
    continuous: '连续重复',
    minutes: '分钟',
    save: '保存告警规则',
    saved: '告警规则已保存。',
    toggleRule: '启用或停用{rule}',
    thresholdFor: '{rule}的阈值',
    durationFor: '{rule}的持续时间',
    strategyFor: '{rule}的通知策略',
  },
  node: {
    allNodes: '所有节点',
    detail: '节点详情',
    serverUptime: '服务器运行时间',
    lastSample: '最新样本',
    controllerLatency: '控制端延迟',
    latencyDetail: '到此 Pulsewatch 控制端的认证 HTTP 往返时间',
    cpuUtilization: 'CPU 使用率',
    cpu: 'CPU',
    user: '用户',
    memory: '内存',
    storage: '存储',
    used: '已用',
    totalNetworkRate: '当前网络速率',
    interfaces: '{count} 个网络接口',
    history: '历史',
    signals: '资源趋势',
    timeRange: '时间范围',
    lastHour: '最近 1 小时',
    last6Hours: '最近 6 小时',
    last24Hours: '最近 24 小时',
    last7Days: '最近 7 天',
    last30Days: '最近 30 天',
    customRange: '自定义范围',
    from: '开始时间',
    to: '结束时间',
    refreshing: '刷新中...',
    cpuDetail: '用户、系统和总使用率',
    total: '总计',
    system: '系统',
    memoryDetail: '物理内存和交换空间使用率',
    ram: '物理内存',
    swap: '交换空间',
    network: '网络吞吐量',
    networkDetail: '汇总接收和发送速率',
    receive: '接收',
    transmit: '发送',
    totalTraffic: '总流量',
    disk: '磁盘吞吐量',
    diskDetail: '汇总物理磁盘读写速率',
    read: '读取',
    write: '写入',
    load: '系统负载',
    loadDetail: '1、5 和 15 分钟系统平均负载',
    oneMin: '1 分钟',
    fiveMin: '5 分钟',
    fifteenMin: '15 分钟',
    analytics: '分析模式',
    intelligence: '容量分析',
    analyticsDisabled: '高级分析已停用',
    analyticsDisabledMessage:
      '请在至少 4 核 CPU、8 GB 内存的服务器上切换到分析模式，以启用百分位、容量预测和异常分析。',
    p95Bandwidth: '带宽第 95 百分位',
    p95Detail: '适用于计费分析，排除最高 5% 峰值',
    p99Bandwidth: '带宽第 99 百分位',
    p99Detail: '接近最大值的持续吞吐量',
    anomalies: '检测到的异常',
    anomaliesDetail: '此范围内的统计异常值',
    forecasts: '资源预测',
    perDay: '每天 {value}',
    insufficient: '趋势数据不足',
    pointsDay: '每天 {value} 个百分点',
    forecastEmpty: '需要更多历史数据才能生成可靠预测。',
    peaks: '使用高峰时段',
    around: '{time}左右',
    peakEmpty: '暂时没有可用的周期性高峰时段。',
    outliers: '近期异常值',
    time: '时间',
    metric: '指标',
    value: '数值',
    deviation: '偏差',
    resourceTarget: '{resource} · {target}',
    analyticsLabels: {
      memory: '内存',
      disk: '磁盘',
      cpuPercent: 'CPU 使用率',
      memoryPercent: '内存使用率',
      diskMaxPercent: '磁盘使用率',
      networkBytesPerSecond: '网络吞吐量',
      utilization: '使用率',
      insufficientData: '数据不足',
      notExhausting: '暂未接近容量上限',
      alreadyAtOrAboveCapacity: '已达到或超过容量上限',
      forecast: '可提供容量预测',
      stable: '稳定',
      unknown: '其他指标',
    },
  },
  admin: {
    eyebrow: '系统管理',
    title: '管理设置',
    subtitle: '管理外发邮件和受保护的系统备份。',
    emailTab: '邮件系统',
    backupTab: '备份管理',
    smtpTitle: 'SMTP 配置',
    smtpSubtitle: '凭据为只写字段，API 永远不会返回其内容。',
    smtpHost: 'SMTP 主机',
    smtpPort: '端口',
    senderAddress: '发件人地址',
    smtpUsername: '用户名',
    smtpPassword: '密码',
    secretStored: '已保存密钥（留空则保留）',
    connectionSecurity: '连接安全方式',
    implicitTls: '隐式 TLS（通常使用 465 端口）',
    starttls: 'STARTTLS（通常使用 587 端口）',
    noEncryption: '不加密（不推荐）',
    clearStoredPassword: '清除已保存密码',
    testRecipient: '测试收件人',
    testRecipientPlaceholder: "administrator{'@'}example.com",
    testRecipientHint: '测试会使用当前表单内容，但不会保存这些设置。',
    templateTitle: '邮件模板',
    templateSubtitle: '故障告警和恢复通知使用独立的 HTML 模板。',
    alertTemplate: '告警模板',
    recoveryTemplate: '恢复模板',
    placeholders: '可用占位符',
    insertPlaceholder: '在光标位置插入占位符',
    templateEditor: 'HTML 邮件模板编辑器',
    alertTemplateEditor: '告警 HTML 模板编辑器',
    recoveryTemplateEditor: '恢复 HTML 模板编辑器',
    livePreview: '实时预览',
    previewTitle: '告警邮件渲染预览',
    alertPreviewTitle: '告警邮件渲染预览',
    recoveryPreviewTitle: '恢复邮件渲染预览',
    previewNode: '边缘节点 01',
    previewReason: 'CPU 使用率超过阈值',
    previewAlertReason: 'CPU 使用率触发告警：94.2% >= 有效阈值 90.0%。',
    previewRecoveryReason: 'CPU 使用率已恢复：42.0% < 有效阈值 90.0%。',
    emailSaved: '邮件系统设置已保存。',
    saveEmail: '保存邮件系统',
    sendTestEmail: '发送测试邮件',
    testingEmail: '正在发送测试邮件...',
    testEmailSent: '测试邮件已发送至 {recipient}。',
    manualBackup: '立即备份',
    manualBackupDetail: '立即创建一致的数据库和历史归档备份包。',
    startingBackup: '正在启动...',
    backupNow: '立即备份',
    backupConfiguration: '备份配置',
    scheduledBackups: '启用定时备份',
    everyDays: '每 X 天运行一次',
    storageTarget: '存储目标',
    localBackup: '本地备份',
    remoteBackup: '远程备份',
    endpointUrl: '端点 URL',
    region: '区域',
    bucketName: '存储桶名称',
    accessKey: '访问密钥',
    secretKey: '私密密钥',
    clearStoredSecret: '清除已保存的私密密钥',
    saveBackup: '保存备份设置',
    backupSettingsSaved: '备份设置已保存。',
    backupQueued: '备份已启动，历史记录将自动更新。',
    backupHistory: '备份历史',
    file: '文件',
    created: '创建时间',
    target: '目标',
    size: '大小',
    statusLabel: '状态',
    actions: '操作',
    download: '下载',
    noBackups: '尚未创建任何备份。',
    status: {
      pending: '等待中',
      running: '进行中',
      completed: '已完成',
      failed: '失败',
    },
  },
}

export type SupportedLocale = 'en' | 'zh-CN'

export const i18n = createI18n({
  legacy: false,
  locale: 'en',
  fallbackLocale: 'en',
  messages: { en, 'zh-CN': zhCN },
})
